import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Api from "../../../../shared/api/api";

import "../../styles/DevelopmentAdmin.css";
import "../styles/DevelopmentSettings.css";

const EMPTY_FORM = {
  setting_key: "",
  setting_value: "",
  value_type: "string",
  description: "",
};

const VALUE_TYPES = [
  "string",
  "number",
  "boolean",
  "json",
  "email",
  "url",
  "text",
];

function formatLabel(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    }
  ).format(date);
}

function normalizeSetting(setting = {}) {
  return {
    ...EMPTY_FORM,
    ...setting,

    setting_value:
      setting.setting_value == null
        ? ""
        : String(setting.setting_value),

    value_type:
      setting.value_type ||
      "string",
  };
}

function validateSettingValue(
  value,
  valueType
) {
  const trimmedValue =
    String(value ?? "").trim();

  if (
    valueType === "number" &&
    trimmedValue !== ""
  ) {
    const parsed = Number(
      trimmedValue
    );

    if (!Number.isFinite(parsed)) {
      return "The setting value must be a valid number.";
    }
  }

  if (
    valueType === "boolean" &&
    ![
      "true",
      "false",
      "1",
      "0",
    ].includes(
      trimmedValue.toLowerCase()
    )
  ) {
    return 'Boolean settings must use "true", "false", "1", or "0".';
  }

  if (
    valueType === "json" &&
    trimmedValue !== ""
  ) {
    try {
      JSON.parse(trimmedValue);
    } catch {
      return "The setting value must contain valid JSON.";
    }
  }

  if (
    valueType === "email" &&
    trimmedValue !== ""
  ) {
    const emailPattern =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      !emailPattern.test(
        trimmedValue
      )
    ) {
      return "The setting value must be a valid email address.";
    }
  }

  if (
    valueType === "url" &&
    trimmedValue !== ""
  ) {
    try {
      new URL(trimmedValue);
    } catch {
      return "The setting value must be a valid URL.";
    }
  }

  return "";
}

function DevelopmentSettings() {
  const [settings, setSettings] =
    useState([]);

  const [
    selectedSetting,
    setSelectedSetting,
  ] = useState(null);

  const [formData, setFormData] =
    useState(EMPTY_FORM);

  const [showForm, setShowForm] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [typeFilter, setTypeFilter] =
    useState("");

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSaving, setIsSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const loadSettings = useCallback(
    async () => {
      setIsLoading(true);
      setError("");

      try {
        const response =
          await Api.getDevelopmentSettings();

        setSettings(
          response?.settings || []
        );
      } catch (requestError) {
        setError(
          requestError.message ||
            "Development settings could not be loaded."
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const filteredSettings = useMemo(() => {
    const normalizedSearch =
      search.trim().toLowerCase();

    return settings.filter(
      (setting) => {
        if (
          typeFilter &&
          setting.value_type !==
            typeFilter
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const searchableText = [
          setting.setting_key,
          setting.setting_value,
          setting.value_type,
          setting.description,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(
          normalizedSearch
        );
      }
    );
  }, [
    settings,
    search,
    typeFilter,
  ]);

  const summary = useMemo(() => {
    const types = new Set(
      settings.map(
        (setting) =>
          setting.value_type ||
          "string"
      )
    );

    const emptyValues =
      settings.filter(
        (setting) =>
          setting.setting_value ==
            null ||
          String(
            setting.setting_value
          ).trim() === ""
      ).length;

    return {
      total: settings.length,
      types: types.size,
      configured:
        settings.length - emptyValues,
      empty: emptyValues,
    };
  }, [settings]);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const openNewForm = () => {
    clearMessages();
    setSelectedSetting(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (
    setting
  ) => {
    clearMessages();

    setSelectedSetting(setting);

    setFormData(
      normalizeSetting(setting)
    );

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const closeForm = () => {
    setSelectedSetting(null);
    setFormData(EMPTY_FORM);
    setShowForm(false);
  };

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleBooleanValue = (
    value
  ) => {
    setFormData((current) => ({
      ...current,
      setting_value: value,
    }));
  };

  const validateForm = () => {
    if (
      !formData.setting_key.trim()
    ) {
      return "Setting key is required.";
    }

    const keyPattern =
      /^[a-z0-9_]+$/;

    if (
      !keyPattern.test(
        formData.setting_key.trim()
      )
    ) {
      return "Setting keys may only contain lowercase letters, numbers, and underscores.";
    }

    return validateSettingValue(
      formData.setting_value,
      formData.value_type
    );
  };

  const buildPayload = () => ({
    setting_key:
      formData.setting_key.trim(),

    setting_value:
      formData.setting_value === ""
        ? null
        : formData.setting_value,

    value_type:
      formData.value_type,

    description:
      formData.description.trim() ||
      null,
  });

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();
    clearMessages();

    const validationError =
      validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);

    try {
      const payload =
        buildPayload();

      if (selectedSetting) {
        await Api.updateDevelopmentSetting(
          selectedSetting.id,
          payload
        );

        setSuccess(
          "Setting updated successfully."
        );
      } else {
        await Api.createDevelopmentSetting(
          payload
        );

        setSuccess(
          "Setting created successfully."
        );
      }

      await loadSettings();
      closeForm();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The setting could not be saved."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSetting = async (
    setting
  ) => {
    const confirmed =
      window.confirm(
        `Delete the setting "${setting.setting_key}"?`
      );

    if (!confirmed) {
      return;
    }

    clearMessages();

    try {
      await Api.deleteDevelopmentSetting(
        setting.id
      );

      setSuccess(
        "Setting deleted successfully."
      );

      await loadSettings();
    } catch (requestError) {
      setError(
        requestError.message ||
          "The setting could not be deleted."
      );
    }
  };

  const duplicateSetting = (
    setting
  ) => {
    clearMessages();
    setSelectedSetting(null);

    setFormData({
      ...normalizeSetting(setting),
      setting_key:
        `${setting.setting_key}_copy`,
    });

    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="development-settings-page">
      <div className="development-admin-page-heading">
        <div>
          <p className="development-admin-eyebrow">
            Application configuration
          </p>

          <h2>Settings</h2>

          <p>
            Manage Development business,
            document, billing, notification,
            and application configuration
            values.
          </p>
        </div>

        <div className="development-admin-heading-actions">
          <button
            type="button"
            className="development-admin-button primary"
            onClick={openNewForm}
          >
            Add setting
          </button>
        </div>
      </div>

      {error && (
        <div
          className="development-admin-alert error"
          role="alert"
        >
          {error}
        </div>
      )}

      {success && (
        <div
          className="development-admin-alert success"
          role="status"
        >
          {success}
        </div>
      )}

      {showForm && (
        <SettingsForm
          formData={formData}
          selectedSetting={
            selectedSetting
          }
          isSaving={isSaving}
          onChange={handleChange}
          onBooleanValue={
            handleBooleanValue
          }
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      <section className="development-setting-summary-grid">
        <SummaryCard
          label="All settings"
          value={summary.total}
          onClick={() => {
            setSearch("");
            setTypeFilter("");
          }}
        />

        <SummaryCard
          label="Value types"
          value={summary.types}
          onClick={() =>
            setTypeFilter("")
          }
        />

        <SummaryCard
          label="Configured"
          value={summary.configured}
          onClick={() =>
            setSearch("")
          }
        />

        <SummaryCard
          label="Empty values"
          value={summary.empty}
          onClick={() =>
            setSearch("")
          }
        />
      </section>

      <section className="development-settings-panel">
        <div className="development-settings-toolbar">
          <div>
            <h3>
              Configuration records
            </h3>

            <p>
              {filteredSettings.length} setting
              {filteredSettings.length ===
              1
                ? ""
                : "s"}{" "}
              shown
            </p>
          </div>

          <div className="development-setting-filters">
            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search settings..."
              aria-label="Search settings"
            />

            <select
              value={typeFilter}
              onChange={(event) =>
                setTypeFilter(
                  event.target.value
                )
              }
              aria-label="Filter by value type"
            >
              <option value="">
                All value types
              </option>

              {VALUE_TYPES.map(
                (valueType) => (
                  <option
                    key={valueType}
                    value={valueType}
                  >
                    {formatLabel(
                      valueType
                    )}
                  </option>
                )
              )}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div
            className="development-admin-state"
            role="status"
          >
            Loading settings...
          </div>
        ) : filteredSettings.length ===
          0 ? (
          <div className="development-admin-empty">
            No settings match the current
            filters.
          </div>
        ) : (
          <div className="development-setting-table-wrapper">
            <table className="development-setting-table">
              <thead>
                <tr>
                  <th>Setting</th>
                  <th>Value</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>

              <tbody>
                {filteredSettings.map(
                  (setting) => (
                    <tr key={setting.id}>
                      <td>
                        <strong className="development-setting-key">
                          {
                            setting.setting_key
                          }
                        </strong>
                      </td>

                      <td>
                        <SettingValue
                          setting={
                            setting
                          }
                        />
                      </td>

                      <td>
                        <span className="development-setting-type">
                          {formatLabel(
                            setting.value_type
                          )}
                        </span>
                      </td>

                      <td>
                        <span className="development-setting-description">
                          {setting.description ||
                            "No description"}
                        </span>
                      </td>

                      <td>
                        {formatDate(
                          setting.updated_at
                        )}
                      </td>

                      <td>
                        <div className="development-setting-actions">
                          <button
                            type="button"
                            onClick={() =>
                              openEditForm(
                                setting
                              )
                            }
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              duplicateSetting(
                                setting
                              )
                            }
                          >
                            Duplicate
                          </button>

                          <button
                            type="button"
                            className="delete"
                            onClick={() =>
                              deleteSetting(
                                setting
                              )
                            }
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SettingValue({
  setting,
}) {
  const value =
    setting.setting_value;

  if (
    value == null ||
    String(value).trim() === ""
  ) {
    return (
      <span className="development-setting-empty-value">
        Empty
      </span>
    );
  }

  if (
    setting.value_type ===
    "boolean"
  ) {
    const enabled = [
      "true",
      "1",
    ].includes(
      String(value).toLowerCase()
    );

    return (
      <span
        className={`development-setting-boolean ${
          enabled
            ? "enabled"
            : "disabled"
        }`}
      >
        {enabled
          ? "Enabled"
          : "Disabled"}
      </span>
    );
  }

  if (
    setting.value_type === "url"
  ) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="development-setting-link"
      >
        {value}
      </a>
    );
  }

  if (
    setting.value_type ===
    "email"
  ) {
    return (
      <a
        href={`mailto:${value}`}
        className="development-setting-link"
      >
        {value}
      </a>
    );
  }

  if (
    setting.value_type === "json"
  ) {
    return (
      <code className="development-setting-code">
        {value}
      </code>
    );
  }

  return (
    <span className="development-setting-value">
      {value}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  onClick,
}) {
  return (
    <button
      type="button"
      className="development-setting-summary-card"
      onClick={onClick}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function SettingsForm({
  formData,
  selectedSetting,
  isSaving,
  onChange,
  onBooleanValue,
  onSubmit,
  onCancel,
}) {
  return (
    <section className="development-setting-form-panel">
      <div className="development-setting-form-heading">
        <div>
          <p className="development-admin-eyebrow">
            {selectedSetting
              ? "Edit setting"
              : "New setting"}
          </p>

          <h3>
            {selectedSetting
              ? selectedSetting.setting_key
              : "Create setting"}
          </h3>
        </div>

        <button
          type="button"
          className="development-setting-close-button"
          onClick={onCancel}
          aria-label="Close setting form"
        >
          ×
        </button>
      </div>

      <form
        className="development-setting-form"
        onSubmit={onSubmit}
      >
        <div className="development-setting-form-grid">
          <label className="development-setting-field">
            <span>
              Setting key *
            </span>

            <input
              type="text"
              name="setting_key"
              value={
                formData.setting_key
              }
              onChange={onChange}
              placeholder="business_email"
              required
            />

            <small>
              Use lowercase letters,
              numbers, and underscores.
            </small>
          </label>

          <label className="development-setting-field">
            <span>Value type</span>

            <select
              name="value_type"
              value={
                formData.value_type
              }
              onChange={onChange}
            >
              {VALUE_TYPES.map(
                (valueType) => (
                  <option
                    key={valueType}
                    value={valueType}
                  >
                    {formatLabel(
                      valueType
                    )}
                  </option>
                )
              )}
            </select>
          </label>

          <div className="development-setting-field full-width">
            <span>Setting value</span>

            {formData.value_type ===
            "boolean" ? (
              <div className="development-setting-boolean-input">
                <button
                  type="button"
                  className={
                    [
                      "true",
                      "1",
                    ].includes(
                      String(
                        formData.setting_value
                      ).toLowerCase()
                    )
                      ? "active"
                      : ""
                  }
                  onClick={() =>
                    onBooleanValue(
                      "true"
                    )
                  }
                >
                  Enabled
                </button>

                <button
                  type="button"
                  className={
                    [
                      "false",
                      "0",
                    ].includes(
                      String(
                        formData.setting_value
                      ).toLowerCase()
                    )
                      ? "active disabled"
                      : ""
                  }
                  onClick={() =>
                    onBooleanValue(
                      "false"
                    )
                  }
                >
                  Disabled
                </button>
              </div>
            ) : formData.value_type ===
                "text" ||
              formData.value_type ===
                "json" ? (
              <textarea
                name="setting_value"
                value={
                  formData.setting_value
                }
                onChange={onChange}
                rows={
                  formData.value_type ===
                  "json"
                    ? 10
                    : 6
                }
                placeholder={
                  formData.value_type ===
                  "json"
                    ? '{\n  "example": true\n}'
                    : ""
                }
              />
            ) : (
              <input
                type={
                  formData.value_type ===
                  "number"
                    ? "number"
                    : formData.value_type ===
                        "email"
                      ? "email"
                      : formData.value_type ===
                          "url"
                        ? "url"
                        : "text"
                }
                name="setting_value"
                value={
                  formData.setting_value
                }
                onChange={onChange}
                step={
                  formData.value_type ===
                  "number"
                    ? "any"
                    : undefined
                }
              />
            )}
          </div>

          <label className="development-setting-field full-width">
            <span>Description</span>

            <textarea
              name="description"
              value={
                formData.description
              }
              onChange={onChange}
              rows="4"
              placeholder="Explain what this setting controls..."
            />
          </label>
        </div>

        <div className="development-setting-form-actions">
          <button
            type="button"
            className="development-admin-button secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="development-admin-button primary"
            disabled={isSaving}
          >
            {isSaving
              ? "Saving..."
              : selectedSetting
                ? "Save changes"
                : "Create setting"}
          </button>
        </div>
      </form>
    </section>
  );
}

export default DevelopmentSettings;